import { array as codecArray } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, paginated, requestParams } from '@codegrade/api-core';
import { AccessPassCoupon, AccessPassCouponUsage, CouponDataParser, CreateAccessPlanData, PayWithCouponAccessPlanData, StartedTransaction, StartPaymentAccessPlanData, TenantAccessPlan, UpdateAccessPlanData, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function accessPlan(config) {
    const createResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(TenantAccessPlan)),
    });
    const getAllResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(TenantAccessPlan))),
    });
    const createCouponResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AccessPassCoupon)),
    });
    const getCouponsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(AccessPassCoupon))),
    });
    const deleteCouponResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const updateCouponResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AccessPassCoupon)),
    });
    const startPaymentResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(StartedTransaction)),
    });
    const updateResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(TenantAccessPlan)),
    });
    const payWithCouponResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AccessPassCouponUsage)),
    });
    return Object.freeze({
        /**
         * Create a new tenant-wide access plan.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns The newly created access plan.
         */
        create: async ({ requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/access_plans/`, createResponse.v, JSON.stringify(CreateAccessPlanData.encode(requestBody)));
            return result;
        },
        /**
         * Get all access plans for a tenant.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The ID of the tenant to get the access plans for.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns A paginated list of access plans for the tenant.
         */
        getAll: async ({ tenantId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/access_plans/`, getAllResponse.v, undefined, {
                tenant_id: tenantId,
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Create a coupon for a tenant access plan.
         *
         * @param arg The arguments to the function.
         * @param arg.planId The ID of the access plan.
         * @param arg.requestBody The body of the request.
         * @returns The created coupon.
         */
        createCoupon: async ({ planId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/access_plans/${planId}/coupons/`, createCouponResponse.v, JSON.stringify(CouponDataParser.encode(requestBody)));
            return result;
        },
        /**
         * Get all coupons for a tenant access plan.
         *
         * @param arg The arguments to the function.
         * @param arg.planId The ID of the access plan.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns A paginated list of coupons.
         */
        getCoupons: async ({ planId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/access_plans/${planId}/coupons/`, getCouponsResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Delete an access pass coupon.
         *
         * @param arg The arguments to the function.
         * @param arg.planId The ID of the access plan.
         * @param arg.couponId The ID of the coupon to delete.
         * @returns An empty response.
         */
        deleteCoupon: async ({ planId, couponId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/access_plans/${planId}/coupons/${couponId}`, deleteCouponResponse.v, undefined);
            return result;
        },
        /**
         * Update an access pass coupon.
         *
         * @param arg The arguments to the function.
         * @param arg.planId The ID of the access plan.
         * @param arg.couponId The ID of the coupon to update.
         * @param arg.requestBody The body of the request.
         * @returns The updated coupon.
         */
        updateCoupon: async ({ planId, couponId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/access_plans/${planId}/coupons/${couponId}`, updateCouponResponse.v, JSON.stringify(CouponDataParser.encode(requestBody)));
            return result;
        },
        /**
         * Create a new payment for a tenant-wide access plan.
         *
         * This transaction will grant the user access to all courses within the tenant for the duration specified in the plan.
         *
         * @param arg The arguments to the function.
         * @param arg.planId The ID of the access plan to pay for.
         * @param arg.courseId The ID of the course the user was on, to redirect back to after successful payment.
         * @param arg.requestBody The body of the request.
         * @returns A transaction object with a `stripe_url` key that can be used to complete the payment.
         */
        startPayment: async ({ planId, courseId, requestBody, }) => {
            const result = await requestParams(config, 'PUT', `/api/v1/access_plans/${planId}/pay`, startPaymentResponse.v, JSON.stringify(StartPaymentAccessPlanData.encode(requestBody)), {
                course_id: courseId,
            });
            return result;
        },
        /**
         * Update a tenant access plan.
         *
         * @param arg The arguments to the function.
         * @param arg.planId The ID of the plan to update.
         * @param arg.requestBody The body of the request.
         * @returns The updated access plan.
         */
        update: async ({ planId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/access_plans/${planId}`, updateResponse.v, JSON.stringify(UpdateAccessPlanData.encode(requestBody)));
            return result;
        },
        /**
         * Use a coupon to get access to a tenant-wide plan.
         *
         * @param arg The arguments to the function.
         * @param arg.planId The ID of the access plan to pay for.
         * @param arg.requestBody The body of the request.
         * @returns The newly created access pass coupon usage record.
         */
        payWithCoupon: async ({ planId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/access_plans/${planId}/pay_with_coupon/`, payWithCouponResponse.v, JSON.stringify(PayWithCouponAccessPlanData.encode(requestBody)));
            return result;
        },
    });
}
